import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withActivityContext } from '@bingads-webui-react/hoc-with-signal';
import {
  withFilterContext,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { withAdsStudioServicesLoaded } from '../../../services';
import { TargetFolderPickerModal } from './target-folder-picker-modal';

const Modal = ({
  isGlobalSearch,
  sourceFolderId,
  isModalVisible,
  setModalVisible,
  batchMoveAsync,
  onSuccess,
  onError,
  i18n,
  logger,
  ...rest
}) => {
  const onCancel = useCallback(() => {
    setModalVisible(false);
    logger.logTrace('Cancel to batch move');
  }, [logger, setModalVisible]);

  const onMoveHereClick = useCallback((selectedFolderId) => {
    logger.logTrace('Confirm to batch move');
    batchMoveAsync({
      destFolderId: selectedFolderId,
    }).then((result) => {
      setModalVisible(false);
      onSuccess(result);

      const { hasError } = result || {};
      if (hasError) {
        logger.logTrace('Batch move is done with some errors');
      } else {
        logger.logTrace('Batch move successfully');
      }
    }).catch((alODataErr) => {
      const { getUXMessage } = alODataErr;
      // close modal, and show message to user
      setModalVisible(false);
      logger.logError(alODataErr);
      onError(getUXMessage(i18n));
    });
  }, [logger, batchMoveAsync, setModalVisible, onSuccess, onError, i18n]);

  return (
    <TargetFolderPickerModal
      isGlobalSearch={isGlobalSearch}
      sourceFolderId={sourceFolderId}
      show={isModalVisible}
      onMoveHereClick={onMoveHereClick}
      onCancel={onCancel}
      i18n={i18n}
      logger={logger}
      {...rest}
    />
  );
};

Modal.propTypes = {
  isGlobalSearch: PropTypes.bool.isRequired,
  sourceFolderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isModalVisible: PropTypes.bool.isRequired,
  setModalVisible: PropTypes.func.isRequired,
  batchMoveAsync: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
    logError: PropTypes.func,
  }).isRequired,
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }).isRequired,
};

const ModalWithActivityAndDataService = withFilterContext(withActivityContext(withAdsStudioServicesLoaded(Modal)));

// rest:
//   aid, cid, dateFormat, accountTimeZone, oDataConfig, preferenceService, activity, allowedAssetType
const getUseBatchMoveModalHook = ModalComponent => ({ i18n, logger, ...rest }) => {
  const [isModalVisible, setModalVisible] = useState(false);

  const openModal = () => {
    setModalVisible(true);
  };

  const render = useCallback((isGlobalSearch, sourceFolderId, batchMoveAsync, onSuccess, onError) => (
    isModalVisible ?
      <ModalComponent
        isModalVisible={isModalVisible}
        setModalVisible={setModalVisible}
        isGlobalSearch={isGlobalSearch}
        sourceFolderId={sourceFolderId}
        batchMoveAsync={batchMoveAsync}
        onSuccess={onSuccess}
        onError={onError}
        i18n={i18n}
        logger={logger}
        isFoldersEnabled
        {...rest}
      /> : null
  ), [i18n, isModalVisible, logger, rest]);

  return {
    open: openModal,
    render,
  };
};

export const useBatchMoveModal = getUseBatchMoveModalHook(ModalWithActivityAndDataService);

// for test
export const useBatchMoveModalWithoutServices = getUseBatchMoveModalHook(Modal);
